import { createContext, useContext, useEffect, useState, useMemo, useCallback, useRef } from "react";
import { getCurrentUser } from "@/services/auth";
import { User, UserContextType } from "@/types/user";
import posthog, { isLocalEnvironment } from "@/lib/posthog";

export const UserContext = createContext<UserContextType>({
  user: null,
  setUser: () => {},
  loading: true,
  monitorLimitReached: false,
  creditCapReached: false,
  refetchUser: async () => {},
});

export const UserProvider = ({ children }: { children: React.ReactNode }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);

  // Memoize fetchUser to prevent it from changing on every render
  // This is critical because components use refetchUser in useEffect dependencies
  const fetchUser = useCallback(async () => {
    try {
      const userData = await getCurrentUser();
      setUser(userData);
    } catch (error: unknown) {
      // 401 is expected when user is not logged in - handle silently
      // 429 means rate limit - don't clear user state, just skip the refresh
      const axiosError = error as { response?: { status?: number } };
      const status = axiosError?.response?.status;

      if (status === 429) {
        // Rate limited - keep existing user state, don't log them out
        console.warn("Rate limited on /auth/me - skipping user refresh");
        return;
      }

      if (status !== 401) {
        console.error("Failed to fetch user:", error);
      }
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const monitorLimitReached = useMemo(
    () => typeof user?.monitorLimit === "number" && typeof user?.monitorCount === "number" && user.monitorCount >= user.monitorLimit,
    [user?.monitorLimit, user?.monitorCount]
  );

  const creditCapReached = useMemo(() => {
    return (
      user?.creditCap !== null &&
      user?.creditUsage !== null &&
      typeof user?.creditCap === "number" &&
      typeof user?.creditUsage === "number" &&
      user.creditUsage >= user.creditCap
    );
  }, [user?.creditCap, user?.creditUsage]);


  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  // Tie PostHog's identity to the same session as the rest of the app: a
  // known user is identified by the uuid already in their JWT (never email —
  // that can change), and an actual logout (identified -> null) resets so
  // the next visitor on this browser doesn't inherit their identity.
  const identifiedUuid = useRef<string | null>(null);
  useEffect(() => {
    if (loading) return;
    if (user && identifiedUuid.current !== user.uuid) {
      // Same users.is_internal flag the admin overview already excludes
      // team/test accounts by, now a hard stop instead of just a tag: once
      // opted out, PostHog's whole capture pipeline (including the
      // $identify call right below) goes silent for this browser, not just
      // this property being filterable later. opt_in_capturing on the
      // non-internal path matters for a shared machine -- otherwise a real
      // user logging in on a browser a teammate previously used would stay
      // silently opted out from that earlier session. Never opt back in
      // while running locally, though -- that opt-out is unconditional
      // (lib/posthog.ts's isLocalEnvironment check), and a non-internal test
      // signup during local dev would otherwise silently undo it.
      if (user.is_internal) {
        posthog.opt_out_capturing();
      } else if (posthog.has_opted_out_capturing() && !isLocalEnvironment()) {
        posthog.opt_in_capturing();
      }
      posthog.identify(user.uuid, { email: user.email, name: user.name, plan: user.plan, is_internal: !!user.is_internal });
      identifiedUuid.current = user.uuid;
    } else if (!user && identifiedUuid.current) {
      posthog.reset();
      identifiedUuid.current = null;
    }
  }, [user, loading]);

  // Memoize context value to prevent unnecessary re-renders of consumers
  const contextValue = useMemo(
    () => ({
      user,
      setUser,
      loading,
      monitorLimitReached,
      creditCapReached,
      refetchUser: fetchUser,
    }),
    [
      user,
      loading,
      monitorLimitReached,
      creditCapReached,
      fetchUser,
    ]
  );

  return (
    <UserContext.Provider value={contextValue}>
      {children}
    </UserContext.Provider>
  );
};

export const useUser = () => useContext(UserContext);
